'use client';

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import Image from '@/lib/app-image';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface IPassengerDetails {
  origin: string;
  destination: string;
  travelDate: string;
  airlineName: string;
  airlineCode: string;
  passengers: {
    passengerId: number;
    name: string;
  }[];
}

const SelectTrip = ({
  passengerDetails,
  setSelectedPax,
}: {
  passengerDetails: IPassengerDetails[];
  setSelectedPax: (pax: number[]) => void;
}) => {
  const [selectedPax, setSelectedPaxState] = useState([]);

  useEffect(() => {
    setSelectedPax(selectedPax);
  }, [selectedPax, setSelectedPax]);

  // Function to handle checkbox change
  const handleCheckboxChange = (
    passengerId: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.checked) {
      setSelectedPaxState((prev) => [...prev, passengerId]); // Add passengerId to the array
    } else {
      setSelectedPaxState((prev) => prev.filter((id) => id !== passengerId)); // Remove passengerId from the array
    }
  };

  return (
    <div className="mt-2 space-y-4 sm:mt-3 sm:space-y-5 lg:mt-4 lg:space-y-6">
      {/* Header */}
      <h1 className="px-1 text-lg font-semibold text-zinc-800 sm:text-xl lg:text-2xl">
        Please select the flights or passengers you wish to cancel
      </h1>

      {/* Trip Cards */}
      <div className="space-y-3 sm:space-y-4">
        {passengerDetails.map((passenger, index) => (
          <Card
            key={index}
            className="border-light w-full rounded-lg border-2 bg-white shadow-md transition-shadow hover:shadow-lg sm:rounded-xl"
          >
            <CardContent className="p-3 sm:p-4 lg:p-5">
              {/* Flight Header */}
              <div className="flex items-center justify-between border-b border-gray-100 pb-3 sm:pb-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  {/* Airline Logo - Uncomment when images are available */}
                  {/* <div className="
                    w-8 h-8 sm:w-10 sm:h-10
                    rounded-full
                    overflow-hidden
                    flex-shrink-0
                  ">
                    <Image
                      src={`@/assets/airlines/${passenger?.airlineCode}.png`}
                      alt={passenger?.airlineName}
                      className="w-full h-full object-cover"
                    />
                  </div> */}

                  {/* Flight Details */}
                  <div className="min-w-0">
                    <div className="truncate text-base font-semibold text-zinc-800 sm:text-lg">
                      {passenger?.origin} - {passenger?.destination}
                    </div>
                    <div className="flex flex-wrap items-center gap-1 text-xs text-zinc-400 sm:text-sm">
                      <span>{passenger?.travelDate}</span>
                      <span className="hidden sm:inline">•</span>
                      <span>{passenger?.airlineName}</span>
                    </div>
                  </div>
                </div>

                {/* Trip Radio */}
                {/* <RadioGroup defaultValue={`trip-${index}`} className="flex items-center">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={`trip-${index}`}
                      id={`trip-${index}`}
                      className="
                        h-4 w-4 sm:h-5 sm:w-5
                        border-2
                        text-primary
                        border-gray-300
                        focus:ring-2
                        focus:ring-primary/20
                        focus-visible:ring-2
                        focus-visible:ring-primary/20
                        focus-visible:ring-offset-2
                        disabled:opacity-50
                        transition-all
                        data-[state=checked]:border-primary
                        data-[state=checked]:bg-primary
                      "
                      checked={true}
                    />
                    <Label
                      htmlFor={`trip-${index}`}
                      className="
                        hidden
                        text-sm sm:text-base
                        font-medium
                        text-gray-700
                        cursor-pointer
                        select-none
                      "
                    >
                      Selected Trip
                    </Label>
                  </div>
                </RadioGroup> */}
                <RadioGroup
                  value={`trip-${index}`}
                  // onValueChange={}
                >
                  <RadioGroupItem value={`trip-${index}`} id="selected" />
                  <Label htmlFor="selected" />
                </RadioGroup>
              </div>

              {/* Passengers List */}
              <div className="mt-2 space-y-1 sm:mt-3">
                {passenger?.passengers?.map((pax, paxIndex) => (
                  <div
                    key={paxIndex}
                    className="flex items-center justify-between border-b border-gray-100 px-1 py-2 transition-colors hover:bg-gray-50 sm:py-2.5"
                  >
                    <Label
                      htmlFor={`passenger-${index}-${paxIndex}`}
                      className="flex-1 cursor-pointer select-none text-sm font-medium text-zinc-800 sm:text-base"
                    >
                      {pax.name}
                    </Label>

                    <Checkbox
                      id={`passenger-${index}-${paxIndex}`}
                      checked={selectedPax.includes(pax.passengerId as number)}
                      onCheckedChange={(checked) => {
                        handleCheckboxChange(pax.passengerId, {
                          target: { checked },
                        } as React.ChangeEvent<HTMLInputElement>);
                      }}
                      className="focus-visible:ring-primary/20 h-4 w-4 rounded border-2 border-gray-300 text-primary transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-primary data-[state=checked]:bg-primary sm:h-5 sm:w-5"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SelectTrip;
